// Local emulation of the field API routes used while offline. Writes go to the sync queue (outbox).
import { v4 as uuid } from "uuid";
import { storage } from "./storage";
import { TTL_MS, httpError } from "./state";
import { validateTake5 } from "@/lib/take5";
import { drawQuestions } from "@/lib/questionDraw";
import { validateInspection, viewsRequired } from "@/lib/inspection";
import { enqueue, hasPending, conflictFor } from "./sync";

const nowIso = () => new Date().toISOString();
const ok = (data, status = 200) => ({ data, status });
export const cacheKey = (url, params) => (url.startsWith("/activities") && params?.archived ? "/activities?archived" : url);

async function cached(key) {
  const v = await storage.get("cache", key);
  if (v === undefined) throw httpError(503, "Donnée non disponible hors ligne : connectez-vous une fois au réseau pour la télécharger.");
  return v;
}
async function me() {
  const lastSync = await storage.get("meta", "lastSync");
  if (!lastSync || Date.now() - lastSync > TTL_MS) throw httpError(401, "Hors ligne depuis plus de 72 h : reconnectez-vous.");
  return cached("/auth/me");
}
const onlineOnly = () => { throw httpError(503, "Action disponible uniquement en ligne."); };
const recKey = (kind, id) => `${kind}:${id}`;
export const getRecord = (kind, id) => storage.get("records", recKey(kind, id));
const saveRecord = (kind, doc, user) => storage.put("records", recKey(kind, doc.id), { kind, doc, user_id: user.id, org_id: user.org_id });
const sameScope = (r, user) => r.user_id === user.id && (r.org_id || user.org_id) === user.org_id;
async function editable(kind, id, user) {
  const rec = await getRecord(kind, id) || { doc: await cached(`/${kind === "take5" ? "take5s" : "inspections"}/${id}`) };
  if (rec.doc.user_id !== user.id || (rec.org_id && rec.org_id !== user.org_id)) throw httpError(404, "Introuvable");
  const conflict = await conflictFor(id);
  if (conflict) throw httpError(409, `Synchronisation en conflit : ${conflict}`);
  return rec.doc;
}
const err422 = (errors, message) => httpError(422, { message, errors });

function take5Fields(doc, d) {
  const site = d.site_id && d.site_id !== doc.site.id ? doc._sites?.find((s) => s.id === d.site_id) : null;
  const planned = d.duration_minutes ? new Date(new Date(doc.start_at).getTime() + d.duration_minutes * 60000).toISOString() : null;
  return { ...(site ? { site } : {}), task: d.task, location: d.location, mode: d.mode, participants: d.mode === "collectif" ? d.participants : [],
    team_id: d.mode === "collectif" ? d.team_id || null : null, duration_minutes: d.duration_minutes, planned_end_at: planned,
    answers: d.answers, hazards: d.hazards, no_hazard_confirmed: d.no_hazard_confirmed, photo_ids: d.photo_ids || [], updated_at: nowIso() };
}

async function newTake5(user, body) {
  const refs = await cached("/referentials");
  const s = refs.sites.find((x) => x.id === body.site_id);
  if (!s) throw httpError(404, "Site introuvable");
  const id = uuid();
  const at = nowIso();
  const dur = body.duration_minutes || null;
  const collectif = body.mode === "collectif";
  const participants = collectif ? (body.participants || []).map((p) => (typeof p === "string" ? p.trim() : p)).filter((p) => (typeof p === "string" ? p : p.name)) : [];
  const team = collectif && body.team_id ? ((await storage.get("cache", "/teams/mine")) || []).find((t) => t.id === body.team_id) : null;
  const doc = { id, org_id: user.org_id, user_id: user.id, user_name: user.name,
    site: { id: s.id, name: s.name, timezone: s.timezone, time_format: s.time_format }, task: "", location: (body.location || "").trim(), mode: collectif ? "collectif" : "individuel",
    participants, team_id: collectif ? body.team_id || null : null, team_name: team?.name || null,
    responsible_user_id: collectif ? team?.leader_id || user.id : null, responsible_name: collectif ? team?.leader_name || user.name : null,
    duration_minutes: dur, start_at: at, planned_end_at: dur ? new Date(Date.parse(at) + dur * 60000).toISOString() : null, questions: drawQuestions(refs.questions.filter((q) => q.active !== false), !!refs.take5_settings?.vary_wording),
    question_set_version: refs.take5_settings?.question_set_version ?? null,
    answers: {}, hazards: [], no_hazard_confirmed: false, photo_ids: [], status: "brouillon", decision: null, decision_at: null, decisions: [],
    stop_reason: null, archived: false, revision: 1, root_id: id, parent_id: null, superseded_by: null, created_at: at, updated_at: at, offline: true };
  await saveRecord("take5", doc, user);
  await enqueue(user, "take5.create", id, { site_id: s.id, location: doc.location, duration_minutes: dur, mode: doc.mode, team_id: doc.team_id, participants, questions: doc.questions, question_set_version: doc.question_set_version });
  return view(doc);
}
const view = (doc) => ({ ...doc, revisions: [{ id: doc.id, revision: doc.revision, status: doc.status, decision: doc.decision, start_at: doc.start_at }],
  can_edit: doc.status === "brouillon" && !doc.archived });

async function decideTake5(user, id, action, body) {
  const doc = await editable("take5", id, user);
  const at = nowIso();
  let upd;
  if (action === "authorize") {
    if (doc.status !== "brouillon") throw httpError(409, "La décision est déjà prise pour ce Take 5");
    const f = take5Fields(doc, body);
    const errors = validateTake5({ ...doc, ...f }, body);
    if (Object.keys(errors).length) {
      await saveRecord("take5", { ...doc, ...f }, user);
      throw err422(errors, "La tâche n'est pas autorisée : corrigez les points signalés.");
    }
    upd = { ...f, status: "autorisee", decision: "autorisee", decision_at: at, decisions: [...doc.decisions, { decision: "autorisee", at, by: user.id, by_name: user.name }] };
  } else if (action === "stop") {
    if (!["brouillon", "autorisee"].includes(doc.status)) throw httpError(409, "Ce travail est déjà arrêté");
    const reason = (body.reason || "").trim();
    if (!reason) throw err422({ stop_reason: "Indiquez la raison de l'arrêt." }, "Indiquez la raison de l'arrêt.");
    upd = { ...(body.data && doc.status === "brouillon" ? take5Fields(doc, body.data) : {}), status: "arretee", decision: "arretee", decision_at: at, stop_reason: reason,
      decisions: [...doc.decisions, { decision: "arretee", at, by: user.id, by_name: user.name, reason, previous: doc.status }] };
  } else {
    if (doc.status !== "autorisee" || doc.superseded_by) throw httpError(409, "Seule une tâche autorisée en cours peut être déclarée terminée");
    upd = { status: "terminee", completed_at: at, decisions: [...doc.decisions, { decision: "terminee", at, by: user.id, by_name: user.name, previous: "autorisee" }] };
  }
  const next = { ...doc, ...upd, updated_at: at };
  await saveRecord("take5", next, user);
  await enqueue(user, `take5.${action}`, id, body || {});
  return view(next);
}

async function newInspection(user, body) {
  const list = await cached("/equipment");
  const eq = list.find((e) => e.id === body.equipment_id);
  if (!eq || !["INSPECT", "MANAGE"].includes(eq.my_right)) throw httpError(404, "Équipement introuvable");
  const tpl = ((await storage.get("meta", "bundle"))?.templates || []).find((t) => t.id === eq.template_id);
  if (!tpl) throw httpError(503, "Modèle d'inspection non disponible hors ligne.");
  const teams = (await storage.get("cache", "/teams/mine")) || [];
  const team = body.team_id ? teams.find((t) => t.id === body.team_id) : null;
  const at = nowIso();
  const doc = { id: uuid(), org_id: eq.org_id, user_id: user.id, user_name: user.name, performed_by_user_id: user.id,
    performed_by_workplace_id: user.org_id, performed_by_team_id: team?.id || null, performed_by_team_name: team?.name || null,
    equipment_id: eq.id, equipment: { code: eq.code, name: eq.name, status_at_start: eq.status }, site: eq.site,
    template: { id: tpl.id, name: tpl.name }, items: tpl.items.map((i) => ({ item_id: i.id, label: i.label, state: null, comment: "", photo_id: null, criticality: null })),
    photo_views: tpl.photo_views || [], photo_policy: eq.photo_policy, user_change: eq.last_user_id !== user.id, has_meter: eq.has_meter,
    meter_unit: eq.meter_unit, previous_meter_value: eq.last_meter_value ?? null, meter_value: null, meter_photo_id: null, meter_justification: "",
    view_photos: {}, status: "brouillon", result: null, anomaly_count: 0, started_at: at, finalized_at: null, updated_at: at, offline: true };
  await saveRecord("inspection", doc, user);
  await enqueue(user, "inspection.create", doc.id, { equipment_id: eq.id, team_id: team?.id || null, base_status: eq.status });
  return { ...doc, can_edit: true };
}

const mergeItems = (doc, d) => ({ items: doc.items.map((it) => { const n = d.items.find((x) => x.item_id === it.item_id);
  return n ? { ...it, ...n, criticality: n.state === "anomalie" ? n.criticality : null } : it; }),
meter_value: doc.has_meter ? d.meter_value : null, meter_photo_id: doc.has_meter ? d.meter_photo_id : null,
meter_justification: d.meter_justification || "", view_photos: d.view_photos || {}, updated_at: nowIso() });

async function finalizeInspection(user, id, body) {
  const doc = await editable("inspection", id, user);
  if (doc.status !== "brouillon") throw httpError(409, "Inspection finalisée : elle ne peut plus être modifiée");
  const f = { ...doc, ...mergeItems(doc, body) };
  const errors = validateInspection(doc, { ...body, meter_value: body.meter_value ?? "" });
  const anomalies = f.items.filter((i) => i.state === "anomalie");
  const req = viewsRequired(doc, body);
  const used = [...anomalies.map((a) => a.photo_id), ...(doc.has_meter ? [f.meter_photo_id] : []), ...(req ? doc.photo_views.map((v) => f.view_photos[v.id]) : [])].filter(Boolean);
  if (used.length !== new Set(used).size) errors.photos = "Une même photo ne peut pas servir de preuve pour plusieurs points.";
  if (Object.keys(errors).length) {
    await saveRecord("inspection", f, user);
    throw err422(errors, "L'inspection ne peut pas être finalisée : complétez les points signalés.");
  }
  const critical = anomalies.some((a) => a.criticality === "critique");
  const next = { ...f, status: "finalisee", result: critical ? "non_conforme" : anomalies.length ? "conforme_avec_anomalies" : "conforme",
    anomaly_count: anomalies.length, critical_count: anomalies.filter((a) => a.criticality === "critique").length, views_required: req, finalized_at: nowIso() };
  await saveRecord("inspection", next, user);
  await enqueue(user, "inspection.finalize", id, body);
  const list = await storage.get("cache", "/equipment");
  if (list) {
    await storage.put("cache", "/equipment", list.map((e) => (e.id === doc.equipment_id
      ? { ...e, last_user_id: user.id, ...(critical ? { status: "bloque" } : {}), ...(doc.has_meter ? { last_meter_value: f.meter_value } : {}) } : e)));
  }
  return { ...next, can_edit: false };
}

async function localRecords(user) {
  return (await storage.all("records")).map((r) => r.value).filter((r) => sameScope(r, user));
}

function filterEquipment(list, params, cats) {
  const q = (params?.q || "").trim().toLowerCase();
  return list.filter((e) => (!params?.site_id || e.site_id === params.site_id)
    && (!params?.category || (cats[e.type_id] || "equipement") === params.category)
    && (!q || [e.code, e.name, e.type_name, ...(e.identifiers || []).map((i) => i.value)].some((v) => (v || "").toLowerCase().includes(q))));
}

const ROUTES = [
  ["get", /^\/auth\/me$/, () => me()],
  ["get", /^\/referentials$/, () => cached("/referentials")],
  ["get", /^\/teams\/mine$/, () => cached("/teams/mine")],
  ["get", /^\/teams\/([^/]+)\/candidates$/, (u, m) => cached(`/teams/${m[1]}/candidates`)],
  ["get", /^\/equipment$/, async (u, m, b, p) => filterEquipment(await cached("/equipment"), p, (await storage.get("meta", "bundle"))?.type_categories || {})],
  ["get", /^\/equipment\/([^/]+)$/, async (u, m) => {
    const hit = await storage.get("cache", `/equipment/${m[1]}`);
    const e = (await cached("/equipment")).find((x) => x.id === m[1]);
    if (!e && !hit) throw httpError(404, "Équipement introuvable");
    const local = (await localRecords(u)).filter((r) => r.kind === "inspection" && r.doc.equipment_id === m[1]).map((r) => r.doc);
    const base = { inspections: [], meter_readings: [], status_history: [], ...(hit || {}), ...(e || {}) };
    return { ...base, inspections: [...local, ...base.inspections.filter((i) => !local.some((l) => l.id === i.id))] };
  }],
  ["get", /^\/activities$/, async (u, m, b, p) => {
    const base = (await storage.get("cache", cacheKey("/activities", p))) || { take5s: [], inspections: [] };
    if (p?.archived) return base;
    const recs = await localRecords(u);
    const merge = (kind, list, key) => { const loc = recs.filter((r) => r.kind === kind).map((r) => r.doc);
      return [...loc, ...list.filter((x) => !loc.some((l) => l.id === x.id))].sort((a, z) => (z[key] > a[key] ? 1 : -1)); };
    return { take5s: merge("take5", base.take5s, "start_at"), inspections: merge("inspection", base.inspections, "started_at") };
  }],
  ["post", /^\/take5s$/, (u, m, b) => newTake5(u, b)],
  ["get", /^\/take5s\/([^/]+)$/, async (u, m) => { const r = await getRecord("take5", m[1]); if (!r) return cached(`/take5s/${m[1]}`);
    const c = await conflictFor(m[1]); const v = view(r.doc); return c ? { ...v, can_edit: false, sync_conflict: c } : v; }],
  ["put", /^\/take5s\/([^/]+)$/, async (u, m, b) => {
    const doc = await editable("take5", m[1], u);
    if (doc.status !== "brouillon") throw httpError(409, "La décision est déjà prise pour ce Take 5");
    const f = take5Fields({ ...doc, _sites: (await cached("/referentials")).sites }, b);
    await saveRecord("take5", { ...doc, ...f }, u);
    await enqueue(u, "take5.save", m[1], b);
    return { ok: true, updated_at: f.updated_at, planned_end_at: f.planned_end_at };
  }],
  ["post", /^\/take5s\/([^/]+)\/(authorize|stop|complete)$/, (u, m, b) => decideTake5(u, m[1], m[2], b)],
  ["post", /^\/take5s\/([^/]+)\/(revise|pdf)$/, onlineOnly],
  ["post", /^\/inspections$/, (u, m, b) => newInspection(u, b)],
  ["get", /^\/inspections\/([^/]+)$/, async (u, m) => { const r = await getRecord("inspection", m[1]); if (!r) return cached(`/inspections/${m[1]}`);
    const c = await conflictFor(m[1]); return { ...r.doc, can_edit: r.doc.status === "brouillon" && !c, sync_conflict: c }; }],
  ["put", /^\/inspections\/([^/]+)$/, async (u, m, b) => {
    const doc = await editable("inspection", m[1], u);
    if (doc.status !== "brouillon") throw httpError(409, "Inspection finalisée : elle ne peut plus être modifiée");
    const f = mergeItems(doc, b);
    await saveRecord("inspection", { ...doc, ...f }, u);
    await enqueue(u, "inspection.save", m[1], b);
    return { ok: true, updated_at: f.updated_at };
  }],
  ["post", /^\/inspections\/([^/]+)\/finalize$/, (u, m, b) => finalizeInspection(u, m[1], b)],
  ["post", /^\/files$/, async (u, m, form) => {
    const file = form.get("file");
    const id = uuid();
    await storage.put("photos", id, { blob: file, content_type: file.type, linked_type: form.get("linked_type"), linked_id: form.get("linked_id"), user_id: u.id });
    await enqueue(u, "photo.upload", id, { linked_type: form.get("linked_type"), linked_id: form.get("linked_id") });
    return { id, content_type: file.type, size: file.size, created_at: nowIso(), pending: true };
  }],
  ["get", /^\/files\/([^/]+)$/, async (u, m) => { const p = await storage.get("photos", m[1]); if (!p) throw httpError(503, "Photo non disponible hors ligne"); return p.blob; }],
];

export function findRoute(config) {
  const method = (config.method || "get").toLowerCase();
  const url = (config.url || "").split("?")[0];
  for (const [mth, re, fn] of ROUTES) {
    const m = url.match(re);
    if (m && mth === method) return { m, fn, url };
  }
  return null;
}

// Entity routes are served locally while the entity still has unsent operations (server copy is not up to date).
export async function mustStayLocal(config) {
  const url = (config.url || "").split("?")[0];
  const m = url.match(/^\/(take5s|inspections|files)\/([0-9a-f-]{36})/);
  if (m) return m[1] === "files" ? Boolean(await storage.get("photos", m[2])) : hasPending(m[2]);
  if (url === "/files" && config.data instanceof FormData) return hasPending(config.data.get("linked_id"));
  return false;
}

export async function runLocal(route, config) {
  const user = route.url === "/auth/me" ? null : await me();
  let body = config.data;
  if (typeof body === "string") { try { body = JSON.parse(body); } catch { body = {}; } }
  return ok(await route.fn(user, route.m, body || {}, config.params || {}));
}

export { onlineOnly };
